import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { TCEmbeddedModeService } from '@cloud-twin-v2/cloud-twin-shared/core/embedded-mode.service';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { InstanceUpdatesManagerService } from '@cloud-twin-v2/cloud-twin-shared/workers/instance-updates-manager.service';
import { HttpCacheInterceptor } from '@common-modules/cache/http-cache/http-cache.interceptor';
import { ScopedHttpCacheService } from '@common-modules/cache/http-cache/scoped-http-cache.service';
import { DynamicLayoutComponent } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout.component';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutStructure } from '@common-modules/dynamic-layout/models/dynamic-layout-structure';
import { LayoutNodeTypes } from '@common-modules/dynamic-layout/models/layout-node-types';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MasterOperatorConnectItemsSettings } from '../models/master-operator-connect-settings';
import { MasterOperatorSingleSettings } from './master-operator-single-settings';

const COMPONENT_SELECTOR = 'tc-master-operator-single';

@UntilDestroy()
@Component({
    selector: COMPONENT_SELECTOR,
    templateUrl: './master-operator-single.component.html',
    styleUrls: ['./master-operator-single.component.scss'],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpCacheInterceptor,
            multi: true,
        },
        ScopedHttpCacheService,
    ],
    standalone: false
})
export class MasterOperatorSingleComponent implements OnInit {
  @Output() configured = new EventEmitter<MasterOperatorConnectItemsSettings>();

  @Input() singleId: string; // it is its own widget instance key

  private _settings: MasterOperatorSingleSettings;
  get settings(): MasterOperatorSingleSettings {
    return this._settings;
  }
  @Input() set settings(value: MasterOperatorSingleSettings) {
    this._settings = value;

    if (this.settings) {
      if (this.settings.connect) {
        this.onConfigureConnection(this.settings.connect);
      }
      this.buildConfiguredLayout();
    }
  }
  @Input() masterInstanceId: string;

  private _layout: DynamicLayoutComponent;
  get layout(): DynamicLayoutComponent {
    return this._layout;
  }
  @ViewChild(DynamicLayoutComponent) set layout(value: DynamicLayoutComponent) {
    this._layout = value;
    if (this.layout) {
      //this.layout.openFirstPopout();
    }
  }

  // If the widget is already bound to a model instance.
  isConfigured = false;
  dynamicLayoutSettings: DynamicLayoutSettings;
  readonly T_SCOPE = `${TranslateScopes.MasterOperator}.${COMPONENT_SELECTOR}`;
  readonly containerClass = 'dl-container-mo-single';
  private readonly _mapWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'MasterOperatorMapWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.map`,
  };
  private readonly _attributesWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'MasterOperatorAttributesWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.attributes`,
  };
  private readonly _defaultWidgets = [this._mapWidget, this._attributesWidget];

  readonly isEmbedded$: Observable<boolean>;
  constructor(
    private readonly _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private readonly _instanceUpdatesManagerService: InstanceUpdatesManagerService,
    private readonly _embeddedModeService: TCEmbeddedModeService
  ) {
    this.isEmbedded$ = this._embeddedModeService.isEmbedded$;
  }

  ngOnInit(): void {}

  /**
   * Receive new connection settings and apply them to main settings to build the layout.
   */
  onConfigureConnection(connectSettings: MasterOperatorConnectItemsSettings): void {
    if (connectSettings) {
      const connectSettingsClone = globalUtilsHelper.clone(connectSettings, true);
      connectSettingsClone.singleId = this.singleId;
      this._settings = globalUtilsHelper.clone(this.settings, true);
      this.settings.connect = connectSettingsClone;
      // this.buildConfiguredLayout();
      this.afterConnected();
      this.configured.next(connectSettingsClone);
    }
  }

  /**
   * Use complete settings to initialize a dynamic layout.
   */
  private buildConfiguredLayout(): void {
    if (this.settings.connect) {
      // this._scopedHttpCacheService.scopedCacheReady$.pipe(untilDestroyed(this)).subscribe(() => {
      this.isConfigured = true;
      if (this.settings) {
        if (this.settings.innerLayout) {
          this.dynamicLayoutSettings = this.settings.innerLayout;
        } else {
          this.buildDynamicLayoutSettings().subscribe((settings) => {
            this.dynamicLayoutSettings = settings;
            this.dynamicLayoutSettings.debugName = 'SingleLayout';
            this.dynamicLayoutSettings.hasHeaders = !this._embeddedModeService.isEmbedded();
          });
        }
      }
      // });
    } else {
      // If no connect settings are received, show connect component and retrieve them.
      this.isConfigured = false;
    }
  }

  private afterConnected(): void {
    if (this.settings.connect) {
      /*
      this._instanceUpdatesManagerService.registerInstancesFromSettings(
        this.settings.connect.items
      );
      */
    }
  }

  /**
   * Build the dynamic layout.
   */
  buildDynamicLayoutSettings(): Observable<DynamicLayoutSettings> {
    const simpleSettings = globalUtilsHelper.clone(this.settings.layout, true);
    simpleSettings.items = simpleSettings.items
      ? simpleSettings.items.concat(this._defaultWidgets)
      : this._defaultWidgets;

    if (this._embeddedModeService.isEmbedded()) {
      simpleSettings.items = simpleSettings.items.filter(
        (item) => item.widgetName !== 'MasterOperatorAttributesWidgetComponent'
      );
    }

    simpleSettings.items.forEach(
      (x) =>
        (x.params = {
          ...x.params,
          parentWidgetInstanceKey: this.singleId,
          singleId: this.singleId,
          connect: this.settings.connect,
          layout: { ...this.settings.layout },
        })
    );

    return this._dynamicLayoutBuilderService
      .build(simpleSettings, {
        generateWidgetIds: true,
      })
      .pipe(
        map((dynamicLayoutSettings) => {
          dynamicLayoutSettings.defaultStructure =
            this.buildDefaultStructure(dynamicLayoutSettings);

          dynamicLayoutSettings.delegatePersistency = {
            currentId: this.settings.singleId,
            delegateToId: this.settings.groupId,
          };

          return dynamicLayoutSettings;
        })
      );
  }

  private buildDefaultStructure(settings: DynamicLayoutSettings): DynamicLayoutStructure[] {
    if (!settings.items.length) {
      return [
        {
          type: LayoutNodeTypes.Stack,
          content: [],
        },
      ];
    }
    let firstWidgetId;
    const restOfWidgetsStructure = [];
    settings.items.forEach((item, index) => {
      if (index === 0) {
        firstWidgetId = item.widgetInstanceKey;
      } else {
        restOfWidgetsStructure.push({
          type: LayoutNodeTypes.Component,
          widgetInstanceKey: item.widgetInstanceKey,
        });
      }
    });

    const result = [
      {
        type: LayoutNodeTypes.Row,
        content: [
          {
            type: LayoutNodeTypes.Stack,
            width: restOfWidgetsStructure.length !== 0 ? 50 : 100,
            content: [
              {
                type: LayoutNodeTypes.Component,
                widgetInstanceKey: firstWidgetId,
              },
            ],
          },
        ],
      },
    ];

    if (restOfWidgetsStructure.length !== 0) {
      result[0].content.push({
        type: LayoutNodeTypes.Stack,
        width: 50,
        content: restOfWidgetsStructure,
      });
    }

    return result as any;
  }
}
